import React from 'react';
import {
  colors,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Radio } from 'app/components/MuiCompat';
import { createStyles, WithStyles } from 'app/util/styles';
import { withStyles } from 'tss-react/mui';
import { change, Field } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { required } from 'app/util/validators';
import { renderCheckboxBase } from 'app/components/FormFields/_renderCheckboxBase';
import { FormOption } from 'app/models/options/options';

const styles = (theme) => createStyles({
  selected: {
    [theme.breakpoints.down('md')]: {
      background: theme.palette.secondary.main,
      '& span': {
        color: '#ffffff',
      },
    },
  },
  radioLabel: {
    marginTop   : 5,
    marginRight : 0,
    fontWeight  : 400,
    [theme.breakpoints.down('md')]: {
      marginTop    : 0,
      paddingTop   : 2,
      paddingBottom: 2,
      borderBottom : `1px solid ${colors.grey[300]}`,
    },
  },
  radioWrapper: {
    marginTop: 15,
    '& label': {
      marginRight: 0,
      fontWeight : 400,
      [theme.breakpoints.down('md')]: {
        flexDirection : 'row-reverse',
        justifyContent: 'space-between',
        marginLeft    : 0,
        '& span:last-child': {
          marginLeft: 10,
        },
      },
    },
  },
  subOptionsTitle: {
    fontWeight  : 600,
    marginBottom: 4,
    fontSize    : '0.875rem',
    color       : colors.grey[700],
  },
  subOptionsPanel: {
    borderLeft : `3px solid ${colors.grey[300]}`,
    paddingLeft: 16,
    marginTop  : 15,
    [theme.breakpoints.down('md')]: {
      borderLeft : 'none',
      borderTop  : `3px solid ${colors.grey[300]}`,
      paddingLeft: 0,
      paddingTop : 12,
      marginTop  : 8,
    },
  },
});

const renderRadioGroup = (props) => {
  const { classes, input, meta, panelName, panelTriggerValue } = props;

  /* Clear the checkboxes when the radio moves off the option that shows them */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    input.onChange(event);
    if (event.target.value !== panelTriggerValue) {
      meta.dispatch(change(meta.form, panelName, []));
    }
  };

  return (
    <FormControl fullWidth>
      <RadioGroup {...input} onChange={handleChange}>
        {props.options.map((option, index) => (
          <FormControlLabel
            key       = {index}
            control   = {<Radio color='primary' />}
            label     = {option.title}
            value     = {option.value}
            className = {input.value === option.value ? `${classes.radioLabel} ${classes.selected}` : classes.radioLabel}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

type Props = WithStyles<typeof styles> & {
  name               : string;
  onChange?          : (...args: any[]) => void;
  options            : FormOption<any>[];
  values?            : object;
  /* Whether the client has the checkbox panel turned on at all */
  isPanelEnabled     : boolean;
  /* The redux-form field the checkboxes write to */
  panelName          : string;
  panelOptions       : FormOption<any>[];
  panelTitle         : string;
  /* The radio value that shows the checkbox panel */
  panelTriggerValue  : string;
  panelValidate?     : ((value: any) => string | undefined)[];
};

/**
 * Radio buttons on the left with a checkbox panel on the right. The panel shows when the client has it
 * enabled and the selected radio is the trigger value. On mobile the panel stacks below the radios.
 */
export const RadioWithCheckboxPanel = withStyles((props: Props) => {
  const { classes, isPanelEnabled, panelName, panelOptions, panelTitle, panelTriggerValue, panelValidate = [] } = props;
  const { t } = useTranslation();

  const isShowingPanel = isPanelEnabled && props.values?.[props.name] === panelTriggerValue;

  return (
    <Grid container spacing={2} className={classes.radioWrapper}>
      <Grid item xs={12} sm={isShowingPanel ? 6 : 12}>
        <Field
          classes           = {classes}
          component         = {renderRadioGroup}
          name              = {props.name}
          onChange          = {props.onChange}
          options           = {props.options}
          panelName         = {panelName}
          panelTriggerValue = {panelTriggerValue}
          validate          = {[required]}
        />
      </Grid>
      {isShowingPanel && (
        <Grid item xs={12} sm={6}>
          <div className={classes.subOptionsPanel}>
            <Typography className={classes.subOptionsTitle}>
              {t(`question.${panelName}.title`, { defaultValue: panelTitle })}
            </Typography>
            <FormGroup>
              <Field
                component = {renderCheckboxBase}
                name      = {panelName}
                options   = {panelOptions}
                validate  = {panelValidate}
              />
            </FormGroup>
          </div>
        </Grid>
      )}
    </Grid>
  );
}, styles);
